import React, {useEffect, useState} from 'react';
import _ from 'underscore';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ConnectionStatus} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails} from 'instruments/connections/ConnectionDAO';
import ConnectionStatusDisplay from 'instruments/connections/ConnectionStatusDisplay';
import {InstrumentGateway} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {getGateway} from 'instruments/instrument-gateways/InstrumentGatewayDAO';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {ContextMenuExtras} from 'nav/expanded/RenderExpandedListing';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import * as ConnectionContextMenu from './ConnectionContextMenu';

export interface ConnectionListingExtras {}

function GatewayDisplay(props: {gatewayId: string}): JSX.Element {
  const [gateway, setGateway] = useState(null as InstrumentGateway | null);

  useEffect((): void => {
    getGateway(props.gatewayId).then((gateway) => {
      setGateway(gateway);
    });
  }, [props.gatewayId]);

  return <span>{gateway ? gateway.name : 'Loading...'}</span>;
}

function StatusDisplay(props: {connectionId: string}): JSX.Element {
  const [status, setStatus] = useState(null as ConnectionStatus | null);

  useEffect((): void => {
    getInstrumentServiceDetails(props.connectionId).then((instrumentServiceDetails) => {
      setStatus(instrumentServiceDetails.status.value);
    });
  }, [props.connectionId]);

  return status != null ? <ConnectionStatusDisplay status={status} /> : <span>Loading...</span>;
}

export default class ConnectionListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.CONNECTION> {
  listingRendererType = ListingRendererType.CONNECTION;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.CONNECTION} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ConnectionListingGraphQL>
  ): Array<ColumnConfiguration<ConnectionListingGraphQL>> {
    let columns: Array<ColumnConfiguration<ConnectionListingGraphQL>> = _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          width: 200,
        },
        render: (item) => (
          <span key="name" className="sidepanel-primaryLabel">
            {item.humanId}
          </span>
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          flexGrow: 1,
        },
        render: (item) => (
          <span className="u-ellipsis-text">
            {item.name}
            <ArchiveRecordLabel item={item} />
          </span>
        ),
      },
      {
        column: {
          header: 'Associated Equipment',
          accessorKey: ColumnAccessorKey.CONNECTION_EQUIPMENT,
          isCollapsible: false,
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) => (
          <>
            {Array.from(item.associatedEquipment.entries()).map(([index, equipment]) => (
              <BioentityChip
                className="u-mini-spacing-top"
                id={equipment.id}
                isLink={true}
                isInaccessible={!equipment.id}
                key={index}
                size={ChipSize.MINI}
              />
            ))}
          </>
        ),
        isHidden: (_) => !BenchlingConfig.ENABLE_EQUIPMENT,
      },

      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEMA,
          header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEMA_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_SCHEMA_NAME,
        },
        render: (item) => item.schema.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CONNECTION_STATUS,
          header: 'Status',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => <StatusDisplay connectionId={item.id} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CONNECTION_GATEWAY,
          header: 'Benchling gateway',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => <GatewayDisplay gatewayId={item.gatewayId} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.OWNER,
          header: 'Owner',
          flexGrow: 1,
          width: 200,
          isCollapsible: false,
        },
        render: (item) => <div>{item.ownerJson.name}</div>,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ]);
    const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(items);
    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column,
          render: (item: ConnectionListingGraphQL) => {
            const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
              tags: item.fields,
              tagColumns: [column],
            });
            return tagsByColumn.tagFields[column.accessorKey];
          },
        }))
      );
    }

    return columns;
  }

  getNonPluginRowContentHeight(item: ConnectionListingGraphQL): number {
    return ExpandedTableTagUtil.getRowHeight(item.fields);
  }

  openContextMenu(item: ConnectionListingGraphQL, e: BasicEvent, extras: ContextMenuExtras): void {
    ConnectionContextMenu.open(e.nativeEvent, item, extras);
  }
}
